import {useQuery} from '@tanstack/react-query';
import {Table} from 'antd';
import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {Field} from '../../../src/models/interfaces';
import {IndexedCrops} from '../../../src/redux/reducers/crops';
import {fieldDesc} from '../../../src/text/desc';
import {getPostgrestQueryParams} from '../../../src/util/postgrest-query';
import {useApis} from '../apis/ApisContext';
import {State} from '../redux';

const dotStyle = {
  display: 'inline-block',
  width: '10px',
  height: '10px',
  borderRadius: '50%',
};

const dotWarning = {
  ...dotStyle,
  backgroundColor: 'red',
};
const dotOk = {
  ...dotStyle,
  backgroundColor: 'green',
};

const Dot: React.FC<{type: boolean}> = ({type}) => {
  return <span style={type ? dotWarning : dotOk}></span>;
};

export const BrazilFieldESG: React.FC<{
  selectedFieldLevelHarvestIds: string[];
  fields?: Field[];
}> = ({selectedFieldLevelHarvestIds, fields}) => {
  const allCrops: IndexedCrops = useSelector((state: State) => state.crops.crops);
  const {authedFetcher, t} = useApis();
  const {data, isLoading} = useQuery(
    ['brazil_harvest_esg', selectedFieldLevelHarvestIds],
    async () =>
      await authedFetcher({
        method: 'GET',
        path: 'api/brazil_harvest_esg',
        params: getPostgrestQueryParams({column: 'harvest_id', operator: 'in', value: selectedFieldLevelHarvestIds}),
      }),
    {keepPreviousData: true},
  );
  const columns = useMemo(
    () => [
      {
        title: 'Campo',
        dataIndex: 'field_id',
        key: 'field_id',
        sorter: true,
        render: (fieldId: string) => {
          const field = fields?.find(field => field.field_id === fieldId);
          return <span>{field ? fieldDesc(t, allCrops, [], field) : ''}</span>;
        },
      },
      {
        title: 'Embargos IBAMA',
        dataIndex: 'is_embargoed',
        key: 'is_embargoed',
        sorter: true,
        render: (value: boolean) => {
          return <Dot type={value} />;
        },
      },
      {
        title: 'Comunidades Quilombola',
        dataIndex: 'is_quilombola',
        key: 'is_quilombola',
        sorter: true,
        render: (value: boolean) => {
          return <Dot type={value} />;
        },
      },
      {
        title: 'UCs de Proteção Integral',
        dataIndex: 'is_conservation',
        key: 'is_conservation',
        sorter: true,
        render: (value: boolean) => {
          return <Dot type={value} />;
        },
      },
      {
        title: 'Áreas Indígenas',
        dataIndex: 'is_indigenous',
        key: 'is_indigenous',
        sorter: true,
        render: (value: boolean) => {
          return <Dot type={value} />;
        },
      },
    ],
    [fields, allCrops, t],
  );
  return (
    <Table
      dataSource={data}
      loading={isLoading}
      columns={columns}
      pagination={false}
      rowKey={item => item.harvest_id}
    />
  );
};

export const BrazilInsuredESG: React.FC<{
  externalId: null | string;
}> = ({externalId}) => {
  const {authedFetcher} = useApis();
  const {data, isLoading} = useQuery(
    ['brazil_insured_esg', externalId],
    async () =>
      await authedFetcher({
        method: 'GET',
        path: 'api/brazil_insured_esg',
        params: getPostgrestQueryParams({
          column: 'external_id',
          operator: 'eq',
          value: externalId,
        }),
      }),
    {keepPreviousData: true},
  );
  if (!data || !data.length) {
    return null;
  }
  const transposed = [
    {label: 'Trabalho Escravo', value: <Dot type={data[0].is_flagged} />},
    {label: 'Embargos IBAMA', value: <Dot type={data[0].is_embargoed} />},
  ];
  return (
    <Table
      showHeader={false}
      columns={[
        {dataIndex: 'label', key: 'label'},
        {dataIndex: 'value', key: 'value'},
      ]}
      dataSource={transposed}
      loading={isLoading}
      pagination={false}
      rowKey="label"
    />
  );
};
